import React, { useState } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutPanelLeft,
  Users,
  FileText,
  BarChart3,
  Plug,
  PanelLeftClose,
  PanelLeft,
  ScanSearch,
  MonitorSmartphone,
  ChevronsUpDown,
  FolderKanban,
  Truck, Package,
} from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../ui/command';
import { Avatar, AvatarFallback } from '../ui/avatar';
import { EvoluxIconSmall } from '../EvoluxIcon';
import { buildPrivateLabels } from '../../lib/privateLabels';

const projectItems = [
  { to: '/project-overview', icon: LayoutPanelLeft, label: 'Visão do projeto' },
  { to: '/personas', icon: Users, label: 'Personas' },
  { to: '/diagnostics', icon: ScanSearch, label: 'Diagnóstico de Usabilidade' },
  { to: '/analyses', icon: FileText, label: 'Análises Técnicas' },
  { to: '/reports', icon: BarChart3, label: 'Relatórios' },
  { to: '/logistics', icon: Truck, label: 'Logística' },
  { to: '/products', icon: Package, label: 'Produtos' },
  { to: '/integrations', icon: Plug, label: 'Integrações' },
  { to: '/client-portal', icon: MonitorSmartphone, label: 'Portal do cliente' },
];

// Detail routes that should fall back to their listing when switching projects
const DETAIL_TO_LIST = [
  [/^\/analyses\/.+/, '/analyses'],
  [/^\/personas\/.+/, '/personas'],
  [/^\/diagnostics\/.+/, '/diagnostics'],
];

const initialsOf = (name = '') => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return 'P';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const ProjectSelector = () => {
  const { projects, selectedProject, setSelectedProject, privateMode, privateOrder } = useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  const hidden = privateMode && location.pathname === '/dashboard';
  const labels = hidden ? buildPrivateLabels(privateOrder, projects || []) : null;
  const nameOf = (p) => (hidden ? labels[p.id] : p.name);
  const avatarOf = (p) => (hidden ? labels[p.id].slice(-2) : initialsOf(p.name));
  const triggerName = hidden ? 'Projeto oculto' : (selectedProject?.name || 'Selecione um projeto');

  const sorted = [...(projects || [])].sort((a, b) =>
    nameOf(a).localeCompare(nameOf(b), 'pt-BR', { sensitivity: 'base' })
  );

  const handleSelect = (project) => {
    setOpen(false);
    if (selectedProject?.id === project.id) return;
    setSelectedProject(project);
    const path = location.pathname;
    if (path === '/dashboard' || path === '/projects') {
      navigate('/project-overview');
      return;
    }
    for (const [re, dest] of DETAIL_TO_LIST) {
      if (re.test(path)) { navigate(dest); return; }
    }
    // otherwise stay on the same project page
  };

  return (
    <div className="px-2 pt-1 pb-2">
      <p className="sidebar-group-label px-2 pb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground sidebar-label">
        Projeto
      </p>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="w-full inline-flex items-center gap-2 rounded-md border border-border bg-background px-2 py-2 text-sm hover:bg-primary/5 transition-colors"
            title={triggerName}
            data-testid="project-selector-trigger"
          >
            <Avatar className="h-7 w-7 shrink-0">
              <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                {selectedProject && !hidden ? initialsOf(selectedProject.name) : <FolderKanban size={14} />}
              </AvatarFallback>
            </Avatar>
            <span className="sidebar-label flex-1 min-w-0 text-left truncate font-medium" data-testid="project-selector-name">
              {triggerName}
            </span>
            <ChevronsUpDown className="sidebar-label h-4 w-4 shrink-0 text-muted-foreground" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-64 p-0" align="start" data-testid="project-selector-popover">
          <Command>
            <CommandInput placeholder="Buscar projeto…" data-testid="project-selector-search" />
            <CommandList>
              <CommandEmpty>Nenhum projeto encontrado.</CommandEmpty>
              <CommandGroup>
                {sorted.map((project) => (
                  <CommandItem
                    key={project.id}
                    value={nameOf(project)}
                    onSelect={() => handleSelect(project)}
                    data-testid={`project-option-${project.id}`}
                  >
                    <Avatar className="h-6 w-6 mr-2 shrink-0">
                      <AvatarFallback className="bg-primary/10 text-primary text-[10px]">
                        {avatarOf(project)}
                      </AvatarFallback>
                    </Avatar>
                    <span className="truncate">{nameOf(project)}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
            <div className="border-t p-1">
              <button
                type="button"
                onClick={() => { setOpen(false); navigate('/projects'); }}
                className="w-full flex items-center gap-2 px-2 py-2 text-sm rounded-sm text-muted-foreground hover:bg-primary/5 hover:text-primary transition-colors"
                data-testid="project-selector-view-all"
              >
                <FolderKanban className="h-4 w-4" />
                Ver todos os projetos
              </button>
            </div>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export const Sidebar = ({ collapsed = false, onToggle }) => {
  const { user, logout, selectedProject } = useApp();
  const hasProject = !!selectedProject;

  return (
    <div className="sidebar" data-testid="sidebar">
      <div className="sidebar-logo">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <EvoluxIconSmall size={28} />
            <h1 className="text-xl font-bold sidebar-label">
              <span className="text-indigo-600">Evol</span>
              <span className="bg-gradient-to-r from-cyan-500 to-teal-400 bg-clip-text text-transparent">UX</span>
            </h1>
          </div>
          <button
            type="button"
            onClick={onToggle}
            className="sidebar-toggle-btn inline-flex items-center justify-center h-8 w-8 rounded-md text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors"
            title={collapsed ? 'Expandir menu' : 'Recolher menu'}
            aria-label={collapsed ? 'Expandir menu' : 'Recolher menu'}
            data-testid="sidebar-toggle"
          >
            {collapsed ? <PanelLeft size={18} /> : <PanelLeftClose size={18} />}
          </button>
        </div>
        <p className="text-xs text-muted-foreground mt-1 sidebar-label">User Experience Evolution</p>
      </div>

      <nav className="sidebar-nav">
        {/* Project selector */}
        <ProjectSelector />

        {/* Project items */}
        {projectItems.map((item) => {
          const testId = `nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`;
          if (!hasProject) {
            return (
              <div
                key={item.to}
                className="sidebar-nav-item opacity-40 cursor-not-allowed select-none"
                aria-disabled="true"
                title={item.label}
                data-testid={testId}
              >
                <item.icon size={20} />
                <span className="sidebar-label">{item.label}</span>
              </div>
            );
          }
          return (
            <NavLink
              key={item.to}
              to={item.to}
              title={item.label}
              className={({ isActive }) =>
                `sidebar-nav-item ${isActive ? 'active' : ''}`
              }
              data-testid={testId}
            >
              <item.icon size={20} />
              <span className="sidebar-label">{item.label}</span>
            </NavLink>
          );
        })}
      </nav>

      <div className="sidebar-footer">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              className="w-full justify-start gap-2 p-2"
              data-testid="user-menu-trigger"
              title={collapsed ? (user?.name || 'Usuário') : undefined}
            >
              <Avatar className="h-8 w-8 shrink-0">
                <AvatarFallback className="bg-primary text-primary-foreground">
                  {user?.name?.charAt(0) || 'U'}
                </AvatarFallback>
              </Avatar>
              <div className="flex flex-col items-start text-sm sidebar-label min-w-0">
                <span className="font-medium truncate max-w-[150px]">{user?.name || 'Usuário'}</span>
                <span className="text-xs text-slate-600 truncate max-w-[150px]">{user?.email}</span>
              </div>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>Minha Conta</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={logout} data-testid="logout-button">
              Sair
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
};
